import { useRouter } from "next/router";

import { useCallback, useMemo, useState } from "react";

import { isArray, orderBy } from "lodash";
import { twMerge } from "tailwind-merge";

import { faTimes } from "@fortawesome/free-solid-svg-icons";

import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import SearchInput from "components/SearchInput";
import useSignInSignUp from "components/SignInSignUpModal/useSignInSignUp";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher } from "utils/baseFetcher";

import ToolMnaTransactionsDownload from "./Download";
import {
  columns as peersTableColumns,
  usePeersComparisonData,
} from "./PeersTradeComparison";
import {
  exportColumns as transactionsTableColumns,
  useTransactionComparisonData,
} from "./PeersTransactionsComparison";

export default function PeersTradeComparisonHeader() {
  const router = useRouter();
  const { authenticated } = useAuthentication();
  const { toggleModal } = useSignInSignUp();
  const entityId = router.query["entity-id"] as string;
  const [selectedEntity, setSelectedEntity] = useState<any>(null);

  useApi(entityId ? entityUrl(entityId) : null, baseFetcher, {
    onSuccess: (data: any) => {
      setSelectedEntity(data);
    },
  });

  const {
    data: latestContentAvailable,
    isLoading: isLatestContentAvailableLoading,
  } = useApi(!entityId ? "/api/v3/merging-acquisitions" : null, baseFetcher, {
    revalidateOnFocus: false,
  });

  const {
    comparisons: transactionsComparisons,
    isLoading: isTransactionsLoading,
  } = useTransactionComparisonData(entityId, false);

  const { comparisons: peersComparisons, isLoading: isPeersLoading } =
    usePeersComparisonData(entityId, false);

  const [_isPeersExpanded, setIsPeersExpanded] =
    useGlobalState(`mnaPeersIsExpanded`);

  const [_isTransactionExpanded, setIsTransactionsExpanded] = useGlobalState(
    `mnaTransactionsIsExpanded`
  );

  const onSelectEntity = useCallback(
    (entity: any) => {
      setSelectedEntity(entity);
      setIsPeersExpanded(true);
      setIsTransactionsExpanded(false);
      router.replace({
        pathname: router.pathname,
        query: {
          ...router.query,
          ["entity-id"]: entity.id,
        },
      });
    },
    [router, setIsPeersExpanded, setIsTransactionsExpanded]
  );

  const peersCSVRow = useMemo(() => {
    if (!isArray(peersComparisons)) return [];

    const headers = peersTableColumns.map((col) => col.title);
    const rows = peersComparisons.map((item: any) =>
      peersTableColumns
        .map((col) => {
          if (col.dataIndex.includes("pretty_name")) {
            return `${item?.pretty_name} ${
              item.exchange_code ? `(${item.exchange_code})` : ""
            }`;
          }

          if (col.dataIndex.split(".").length > 1) {
            const data = col.dataIndex
              .split(".")
              .reduce((acc: any, curr: any) => {
                return acc?.[curr];
              }, item);
            return JSON.stringify(data ?? "");
          }

          return JSON.stringify(item?.[col.dataIndex] ?? "");
        })
        .join(",")
    );

    return [headers.join(","), ...rows].join("\n");
  }, [peersComparisons]);

  const transactionCSVRow = useMemo(() => {
    if (!isArray(transactionsComparisons)) return [];

    const headers = transactionsTableColumns.map((col) => col.title);
    const rows = orderBy(
      transactionsComparisons,
      "announcement_date",
      "desc"
    ).map((item: any) =>
      transactionsTableColumns
        .map((col) => {
          if (col.dataIndex.includes("target_name")) {
            return `${JSON.stringify(item?.target_name ?? "")} ${
              item.target_country ? `(${item.target_country})` : ""
            }`;
          } else if (col.dataIndex.includes("acquirer")) {
            return `${JSON.stringify(item?.acquirer ?? "")} ${
              item.acquirer_country ? `(${item.acquirer_country})` : ""
            }`;
          }

          return JSON.stringify(item[col.dataIndex] ?? "");
        })
        .join(",")
    );

    return [headers.join(","), ...rows].join("\n");
  }, [transactionsComparisons]);

  const onExportCSV = useCallback(() => {
    const csv = [
      "Trading Peer Comparables",
      "Implied multiples based on reported transactions on a FY basis",
      "",
      peersCSVRow,
      "",
      "Precedent Transaction Comparables",
      "Implied multiples based on reported transactions on a LTM basis",
      transactionCSVRow,
    ].join("\n");

    const csvContent = "data:text/csv;charset=utf-8," + csv;
    const encodedUri = encodeURI(csvContent);
    const a = document.createElement("a");
    a.href = encodedUri;
    a.download = `smartkarma-${selectedEntity?.slug}-comparables.csv`;
    a.click();
  }, [peersCSVRow, selectedEntity?.slug, transactionCSVRow]);

  const renderEntitySnippet = useCallback(
    (item: any) => {
      return (
        <div
          key={item.id}
          className={twMerge(
            "border border-solid border-smart-teal rounded-full flex items-center justify-center py-2 text-smart-teal px-6",
            "hover:bg-smart-teal hover:text-white cursor-pointer transition-all"
          )}
          onClick={() => onSelectEntity(item.entity)}
        >
          {item.entity.pretty_name} ({item.entity.bloomberg_ticker})
        </div>
      );
    },
    [onSelectEntity]
  );

  const latestContent = useMemo(() => {
    if (entityId || isLatestContentAvailableLoading) return null;

    return (
      <div className="mb-[100px]">
        <h5>Recently Generated Comparables</h5>
        <div className="flex flex-wrap gap-3 mt-[20px]">
          {latestContentAvailable?.map(renderEntitySnippet)}
        </div>
      </div>
    );
  }, [
    entityId,
    isLatestContentAvailableLoading,
    latestContentAvailable,
    renderEntitySnippet,
  ]);

  return (
    <div className="">
      <div className="flex justify-between">
        <div className="relative flex items-center mb-[20px] border-[1px] border-solid border-smart-teal rounded-md">
          <div
            onClick={() => {
              if (!authenticated) {
                toggleModal("sign-up");
              }
            }}
            className="flex-1"
          >
            <SearchInput
              inputClassName={(isFocusing) =>
                twMerge(
                  isFocusing ? "" : "border-0",
                  "pl-0 py-[20px] rounded-md placeholder:text-[13px] shadow-none"
                )
              }
              className={twMerge(
                "min-w-[400px] flex-1",
                !Boolean(authenticated) && "pointer-events-none cursor-pointer"
              )}
              iconClassName={() => "text-shady-gray"}
              placeholder={selectedEntity?.prettyName || "Select Company"}
              resultPlaceholder="Search by name or Bloomberg Ticker"
              endpoint={"/api/v2/entities"}
              queryParam="filter[fulltext]"
              onItemSelected={onSelectEntity}
            />
          </div>
          <div className="absolute right-0">
            {selectedEntity ? (
              <div
                onClick={() => {
                  setSelectedEntity(null);
                  router.replace({
                    pathname: router.pathname,
                    query: {
                      ...router?.query,
                      "entity-id": undefined,
                    },
                  });
                }}
                className="hover:text-bearish-red text-intelligent-silver cursor-pointer flex items-center justify-center w-[40px] h-[35px]"
              >
                <FontAwesomeIcon icon={faTimes} />
              </div>
            ) : (
              <div className="w-[40px] h-[35px]" />
            )}
          </div>
        </div>
        {entityId && (
          <ToolMnaTransactionsDownload
            isLoading={isTransactionsLoading || isPeersLoading}
            onExportPDF={() => {
              window.open(
                `/embed/comparables?entity-id=${entityId}&export=pdf`
              );
            }}
            onExportPNG={() => {
              window.open(
                `/embed/comparables?entity-id=${entityId}&export=png`
              );
            }}
            onExportCSV={onExportCSV}
          />
        )}
      </div>
      {latestContent}
    </div>
  );
}
